import { useEffect } from 'react';
import { useMailSenderShowParameters } from './useMailSenderShowParameters';
import { useMailSenderListParameters } from './useMailSenderListParameters';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useErrorMessage } from '../../../utils/errorMessage';

/**
 * Retrieves enums (enumerated values) for a specific resource based on the provided parameters.
 *
 * @function
 * @param {Object} params - The parameters for retrieving enums.
 * @param {function} params.dispatchAPI - A function for dispatching API requests.
 * @param {string} params.type - The type of resource.
 * @param {Array} params.pensionerColumns - Columns specific to pensioners.
 * @param {Array} params.ownerColumns - Columns specific to owners.
 * @param {Array} params.veterinarianAndGroomerColumns - Columns specific to veterinarians and groomers.
 * @param {function} params.setEnums - A function to set the enums state.
 * @param {function} params.message - A function for displaying error messages.
 * @param {string} params.purpose - The purpose of retrieving enums (show or create/update).
 */

export const useGetEnums = ({ type, setEnums, purpose }) => {
  const { dispatchAPI } = useAuthContext();
  const { message } = useErrorMessage();

  const showResourceName = useMailSenderShowParameters({
    type
  })?.resourceName;

  const createUpdateResourceName = useMailSenderListParameters({
    type
  })?.resourceName;

  const getEnums = async () => {
    try {
      const { data } = await dispatchAPI('GET', {
        url: `/${
          purpose === 'show' ? showResourceName : createUpdateResourceName
        }/enums`
      });
      setEnums(data);
    } catch (e) {
      message(e);
    }
  };

  useEffect(() => {
    (async () => {
      // Veterinarians and groomers expose no /enums endpoint.
      const resourceName =
        purpose === 'show' ? showResourceName : createUpdateResourceName;
      if (
        resourceName &&
        !['VETERINARIAN_ADVERT', 'GROOMER_ADVERT'].includes(type)
      ) {
        await getEnums();
      }
    })();
  }, []);

  return { getEnums };
};
